<script setup>
import {useMutation, useQueryClient} from "@tanstack/vue-query";
import axios from "axios";
import SquareIconButton from "@/components/global-themed/buttons/SquareIconButton.vue";
import {useStore} from "vuex";
import {computed} from "vue";
import {errorToast, successToast} from "@/composables/useToasts";

defineProps({
  pageId: {
    type: Number,
    required: true,
  },
});

const queryClient = useQueryClient();
const store = useStore();
const activeTab = computed(() => store.getters['actions/activeTab']);

const {mutate: hideActions} = useMutation({
  mutationFn: (pageId) => axios.post(`/ajax/v2/actions/page/${pageId}/hide`),
  onMutate: async (pageId) => {
    await queryClient.cancelQueries({queryKey: ['actions']});
    await queryClient.cancelQueries({queryKey: ['hidden-actions-by-page']});

    const previousActions = queryClient.getQueryData(['actions', activeTab]);

    successToast('Actions hidden for this page')

    const newPagesArray = previousActions?.pages?.map((page) => ({
      ...page,
      data: page.data.filter((val) => val.id !== pageId),
    }) ?? []);

    queryClient.setQueryData(['actions', activeTab], (data) => ({
      pages: newPagesArray,
      pageParams: data.pageParams,
    }));

    return { previousActions };
  },
  onError: (error) => {
    const {message} = error.response.data
    errorToast(message ?? 'Something went wrong hiding actions');
  },
  onSettled: () => {
    queryClient.invalidateQueries({queryKey: ['actions']});
    queryClient.invalidateQueries({queryKey: ['hidden-actions-by-page']});
  },
});
</script>

<template>
  <SquareIconButton
    v-tooltip="{
      content: 'Hide all actions for this page',
      placement: 'bottom',
      theme: 'dark-blue-tooltip',
    }"
    variant="primary-inverted"
    size="small"
    @click="() => hideActions(pageId)"
  >
    <svg-vue
      icon="icons/eye-hidden"
      class="tw-h-6 tw-w-6"
    />
  </SquareIconButton>
</template>
